import { initializeApp } from "firebase/app";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import {
  getAuth,
  User,
  UserCredential,
  NextOrObserver,
  Unsubscribe as AuthUnsubscribe,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  Unsubscribe,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithCustomToken,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  collection,
  doc,
  DocumentReference,
  getDoc,
  getFirestore,
  setDoc,
  updateDoc,
  SetOptions,
  DocumentSnapshot,
  DocumentData,
  onSnapshot,
  where,
  query,
  CollectionReference,
  orderBy,
  getDocs,
  limit,
  startAfter,
} from "firebase/firestore";
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable as firebaseHttpsCallable,
  Functions,
  HttpsCallable,
  HttpsCallableOptions,
  HttpsCallableResult,
} from "firebase/functions";

import {
  ExtendedEmailStatuses,
  Organization,
  EmbedOrganization,
  OrganizationStats,
  AccountSettings,
  OutreachEmailStatuses,
  DbOrganizationSettings,
  OrganizationSettings,
  OrgWebhookStatus,
} from "../types/organizations";
import { BillingInterval, BillingPlanKey } from "../types/organizations";
import type { JobChangeSettings } from "../types/organizations";
import {
  BillingStatus,
  BillingInvoice,
  StartSubscriptionResult,
} from "../types/billing";
import { AppUser, UID, OrgDisplayUser, OutreachUser } from "../types/users";
import { getStatsDateRange } from "../common/utils/date-time";
import { removeUndefineds } from "../common/utils/data";
import { firebaseConfig, isDevelopment, isProduction } from "./config";
import { previewResponse } from "./devPreview";
import { devLog, devWarn } from "../common/utils/devLog";
import dayjs, { Dayjs } from "dayjs";
import { DialerSettings, DialerSettingsResult, MyDialerResult, MyDialerSettings } from "../types/dialer";
import { ExportMappings, ExportSequencesResult, ExportSettingsResult, ExportStagesResult, InboxConversation, InboxMessage, InsightsReport, PhoneEnrichmentSettings, ProductLicenseRules, ProfileEnrichmentSettings } from "../types/addons";
import { AddonKey, AddonStatusResult, CallerIdHealth, CallerIdRecord, LinkedInAutomationSettings, LinkedInAutomationView, UsageReport } from "../types/addons";
import { ExportHistoryQuery, ExportHistoryResult } from "../types/exportHistory";
import { JobChangeAction, JobChangesQuery, JobChangesResult, ResolveJobChangesResult } from "../types/jobChanges";
import { McpAuthorizationRequest, McpConnections } from "../types/mcp";

/**
 * Every backend call. Outside production the dashboard preview
 * (/dev/crm-preview) answers some of them with made-up data (devPreview.ts);
 * everywhere else, and for every other call, this is Firebase's own callable.
 */
const httpsCallable = <Req = unknown, Res = unknown>(functions: Functions, name: string, options?: HttpsCallableOptions): HttpsCallable<Req, Res> => {
  const real = firebaseHttpsCallable<Req, Res>(functions, name, options);
  if (isProduction) return real;
  const call = (data?: Req | null) => (previewResponse(name, data) as Promise<HttpsCallableResult<Res>> | null) ?? real(data);
  return Object.assign(call, real) as HttpsCallable<Req, Res>;
};

export const firebaseApp = initializeApp(firebaseConfig);
export const firebaseAuth = getAuth(firebaseApp);
export const firebaseFirestore = getFirestore(firebaseApp);
export const firebaseStorage = getStorage(firebaseApp);
export const firebaseFunctions = getFunctions(
  firebaseApp,
  isProduction ? "us-central1" : "europe-west3"
);

// Helper function to add timeout to Firebase calls
const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs = 300000 // 5 minutes default
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Request timed out after ${timeoutMs}ms`)), timeoutMs)
    ),
  ]);
};

export type StatsResult = {
  [key: string]: Partial<OrganizationStats>;
};

type Activity = {
  status: OutreachEmailStatuses;
  prospectId: number;
  email?: string;
  didGuess?: boolean;
  optedOut?: boolean;
};

// --------------------
// Emulators
// --------------------

if (isDevelopment) {
  devWarn("Running in development environment");
  connectAuthEmulator(firebaseAuth, "http://localhost:9099");
  connectFunctionsEmulator(firebaseFunctions, "localhost", 5001);
  connectFirestoreEmulator(firebaseFirestore, "localhost", 5002);
}

// --------------------
// Utils
// --------------------

const setDocSafely = <T>(
  docRef: DocumentReference<T>,
  data: T,
  options?: SetOptions
) => {
  const sanitized = structuredClone(data);
  removeUndefineds(sanitized);
  return setDoc(docRef, sanitized, options || {});
};

// --------------------
// Data converters
// --------------------

export const convertDbAccountToAppAccount = (
  dbUser: AppUser | null,
  organization?: Organization
): AppUser | null => {
  const {
    uid,
    email: authEmail,
    emailVerified: authEmailVerified,
  } = firebaseAuth.currentUser || {};

  if (!uid || !authEmail || !dbUser) {
    return null;
  }

  const {
    id: dbOrganizationId,
    name: dbOrganizationName,
    prospectsWebhookId: dbProspectsWebhookId,
    isOutreachAuthorized: dbIsOutreachAuthorized,
    isHubSpotAuthorized: dbIsHubSpotAuthorized,
    ...rest
  } = organization || {};

  const appUser: AppUser = {
    ...rest,
    ...dbUser,
    uid,
    email: authEmail,
    emailVerified: authEmailVerified,
    organizationId: dbOrganizationId,
    organizationName: dbOrganizationName,
    isProspectWebhookCreated: !!dbProspectsWebhookId,
    isOutreachAuthorized: dbIsOutreachAuthorized,
    isHubSpotAuthorized: dbIsHubSpotAuthorized,
  };

  return appUser;
};

export function convertAppOrganizationSettingsToDbOrganizationSettings(
  organizationSettings: OrganizationSettings
): DbOrganizationSettings {
  // No conversion needed - OrganizationSettings now extends DbOrganizationSettings directly
  return organizationSettings as DbOrganizationSettings;
}

export function convertDbOrganizationSettingsToAppOrganizationSettings(
  organizationSettings: DbOrganizationSettings | Organization
): OrganizationSettings {
  // No conversion needed - OrganizationSettings now extends DbOrganizationSettings directly
  return organizationSettings as OrganizationSettings;
}

// --------------------
// Authentication
// --------------------

export const onAuthStateChangedHook = (
  observer: NextOrObserver<User>
): AuthUnsubscribe => onAuthStateChanged(firebaseAuth, observer);

export const signUserUp = (
  email: string,
  password: string
): Promise<UserCredential> =>
  createUserWithEmailAndPassword(firebaseAuth, email, password);

export const signUserIn = (
  email: string,
  password: string
): Promise<UserCredential> =>
  signInWithEmailAndPassword(firebaseAuth, email, password);

export const signUserInWithToken = async (
  token: string
): Promise<UserCredential> => {
  return signInWithCustomToken(firebaseAuth, token);
};

export const resetPassword = (email: string): Promise<void> =>
  sendPasswordResetEmail(firebaseAuth, email);

// The address a sign-in link was sent to, so the link can finish signing in
// when it is opened in the same browser without asking for the email again.
const EMAIL_LINK_KEY = "boring.emailForSignIn";

/** Emails a one-time link that signs the user in without a password. */
export const sendLoginLink = async (email: string, next?: string): Promise<void> => {
  const url = new URL("/auth/login", window.location.origin);
  if (next) url.searchParams.set("next", next);
  await sendSignInLinkToEmail(firebaseAuth, email, {
    url: url.toString(),
    handleCodeInApp: true,
  });
  localStorage.setItem(EMAIL_LINK_KEY, email);
};

export const isLoginLink = (href: string): boolean =>
  isSignInWithEmailLink(firebaseAuth, href);

/** The email the link was sent to from this browser, if any. */
export const getLoginLinkEmail = (): string | null =>
  localStorage.getItem(EMAIL_LINK_KEY);

export const signInWithLoginLink = async (
  email: string,
  href: string
): Promise<UserCredential> => {
  const credential = await signInWithEmailLink(firebaseAuth, email, href);
  localStorage.removeItem(EMAIL_LINK_KEY);
  return credential;
};

/** Emails the signed-in user a link that verifies their address. */
export const sendVerificationEmail = async (): Promise<void> => {
  if (firebaseAuth.currentUser) {
    await sendEmailVerification(firebaseAuth.currentUser, {
      url: `${window.location.origin}/app/select-integration`,
    });
  }
};

const completePendingOrgJoinFunction = httpsCallable<
  Record<string, never>,
  { result: { joined: boolean } | null; status: number; reason?: "unverified" | "not-pending" }
>(firebaseFunctions, "completePendingOrgJoin");
/**
 * Joins the organization a signup matched (by invite or email domain) once
 * the user has verified their email. "unverified" until they click the link.
 */
export const completePendingOrgJoin = async (): Promise<"joined" | "unverified"> => {
  const { data } = await completePendingOrgJoinFunction({});
  return data?.reason === "unverified" ? "unverified" : "joined";
};

export const signUserOut = async (): Promise<void> => {
  await signOut(firebaseAuth);
};

export const reload = () => {
  window.location.reload();
};

// --------------------
// Functions
// --------------------

const getOutreachAuthUrlFunction = httpsCallable<
  { forceReconsent?: boolean },
  { result?: { outreachAuthUrl: string; scopesVersion?: number }; status: number }
>(firebaseFunctions, "getOutreachAuthUrl");
export const getOutreachAuthUrl = async (options?: {
  forceReconsent?: boolean;
}): Promise<{
  result?: { outreachAuthUrl: string; scopesVersion?: number };
  status: number;
}> => {
  const { data } = await getOutreachAuthUrlFunction(options || {});
  if (!data) {
    throw new Error("Error getting Outreach connect url");
  }
  return data;
};

const linkOutreachInstallFunction = httpsCallable<{ installSetupToken: string }, { result: { linked: boolean; reason?: string } | null; status: number }>(
  firebaseFunctions,
  "linkOutreachInstall"
);
/** Links the installation of boring's Outreach app an admin just made to their organization. */
export const linkOutreachInstall = async (installSetupToken: string) => (await linkOutreachInstallFunction({ installSetupToken })).data;

const generateCustomAuthTokenFunction = httpsCallable<
  never,
  {
    result?: { token: string };
  }
>(firebaseFunctions, "generateCustomAuthToken");
export const generateCustomAuthToken = async (): Promise<string> => {
  const { data } = await generateCustomAuthTokenFunction();
  if (!data || !data.result?.token) {
    throw new Error("Error generating custom auth token");
  }
  return data.result.token;
};

const getMyOrganizationFunction = httpsCallable<
  never,
  { result?: Organization; status: number }
>(firebaseFunctions, "getMyOrganization");
export const getMyOrganization = async (): Promise<{
  result?: Organization;
  status: number;
}> => {
  const { data } = await getMyOrganizationFunction();
  if (!data) {
    throw new Error("Error getting organization");
  }
  return data;
};

const getOrgUsersFunction = httpsCallable<
  never,
  { result?: { users: OrgDisplayUser[] }; status: number }
>(firebaseFunctions, "getOrgUsers");
export const getOrgUsers = async (): Promise<{
  result?: { users: OrgDisplayUser[] };
  status: number;
}> => {
  const { data } = await getOrgUsersFunction();
  if (!data) {
    throw new Error("Error getting organization users");
  }
  return data;
};

const inviteOrgUserFunction = httpsCallable<
  { email: string },
  { status: number }
>(firebaseFunctions, "inviteOrgUser");
export const inviteOrgUser = async (
  email: string
): Promise<{
  status: number;
}> => {
  const { data } = await inviteOrgUserFunction({ email });
  return data;
};

const updateOrgSettingsFunction = httpsCallable<
  AccountSettings,
  { result?: Organization; status: number }
>(firebaseFunctions, "updateOrgSettings");
export const updateOrganizationSettings = async (
  userSettings: AccountSettings
): Promise<{
  result?: Organization;
  status: number;
}> => {
  try {
    const { data } = await updateOrgSettingsFunction(userSettings);
    devLog("?? Raw Firebase function response:", { data });
    
    if (!data) {
      console.error("? updateOrganizationSettings: No data returned from function");
      throw new Error("Error updating organization: No data returned");
    }
    
    // If status is not 200, log the error but still return the data
    if (data.status !== 200) {
      console.error("? updateOrganizationSettings: Non-200 status:", data.status);
      console.error("? Response data:", JSON.stringify(data, null, 2));
    }
    
    return data;
  } catch (error: any) {
    console.error("? updateOrganizationSettings exception:", error);
    console.error("? Error details:", {
      message: error?.message,
      code: error?.code,
      details: error?.details,
      stack: error?.stack,
      fullError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
    });
    
    // Firebase Functions errors have a specific structure
    if (error?.code === 'functions/called-failed') {
      console.error("? Firebase function call failed:", error.details);
    }
    
    // If it's a Firebase function error, try to extract the error message
    if (error?.details) {
      throw new Error(`Firebase function error: ${JSON.stringify(error.details)}`);
    }
    throw error;
  }
};

const updateOrganizationAllowedEmailsFunction = httpsCallable<
  { allowedOwnerEmails: string[] },
  { result?: { success: boolean }; status: number }
>(firebaseFunctions, "updateOrganizationAllowedEmails");
export const updateOrganizationAllowedEmails = async (
  allowedOwnerEmails: string[]
): Promise<{
  result?: { success: boolean };
  status: number;
}> => {
  const { data } = await withTimeout(
    updateOrganizationAllowedEmailsFunction({
      allowedOwnerEmails,
    }),
    300000
  );
  if (!data) {
    throw new Error("Error updating organization allowed emails");
  }
  return data;
};

const getOrgWebhookStatusFunction = httpsCallable<
  never,
  { result?: OrgWebhookStatus; status: number }
>(firebaseFunctions, "getOrgWebhookStatus");
export const getOrgWebhookStatus = async (): Promise<{
  result?: OrgWebhookStatus;
  status: number;
}> => {
  const { data } = await getOrgWebhookStatusFunction();
  if (!data) {
    throw new Error("Error getting organization webhook status");
  }
  return data;
};

const updateOrgWebhookStatusFunction = httpsCallable<
  OrgWebhookStatus,
  { result?: OrgWebhookStatus; status: number }
>(firebaseFunctions, "updateOrgWebhookStatus");
export const updateOrgWebhookStatus = async (
  WebhookStatus: OrgWebhookStatus
): Promise<{
  result?: OrgWebhookStatus;
  status: number;
}> => {
  const { data } = await updateOrgWebhookStatusFunction(WebhookStatus);
  if (!data) {
    throw new Error("Error getting organization webhook status");
  }
  return data;
};

const disconnectOutreachFunction = httpsCallable<
  never,
  { result?: boolean; status: number } | null
>(firebaseFunctions, "disconnectOutreach");
export const disconnectOutreach = async (): Promise<{
  result?: boolean;
  status: number;
} | null> => {
  const { data } = await disconnectOutreachFunction();
  return data;
};

const disconnectHubSpotFunction = httpsCallable<
  never,
  { result?: boolean; status: number } | null
>(firebaseFunctions, "disconnectHubSpot");
export const disconnectHubSpot = async (): Promise<{
  result?: boolean;
  status: number;
} | null> => {
  const { data } = await disconnectHubSpotFunction();
  return data;
};

const validateOutreachAuthCodeFunction = httpsCallable<
  string,
  { result?: Organization; status: number }
>(firebaseFunctions, "validateOutreachAuthCode");
export const validateOutreachAuthCode = async (
  outreachAuthCode: string
): Promise<{
  result?: Organization;
  status: number;
}> => {
  const { data } = await validateOutreachAuthCodeFunction(outreachAuthCode);
  if (!data) {
    throw new Error("Error validateOutreachAuthCode");
  }
  return data;
};

const getHubSpotAuthUrlFunction = httpsCallable<
  { redirectUri?: string },
  { result?: { hubspotAuthUrl: string }; status: number }
>(firebaseFunctions, "getHubSpotAuthUrl");
export const getHubSpotAuthUrl = async (redirectUri?: string): Promise<{
  result?: { hubspotAuthUrl: string };
  status: number;
}> => {
  devLog("?? Frontend calling getHubSpotAuthUrl with:", { redirectUri });
  const { data } = await getHubSpotAuthUrlFunction({ redirectUri });
  devLog("?? Frontend received response:", { data });
  if (!data) {
    throw new Error("Error getting HubSpot connect url");
  }
  return data;
};

const validateHubSpotAuthCodeFunction = httpsCallable<
  string | { hubspotAuthCode: string; redirectUri?: string },
  { result?: Organization; status: number }
>(firebaseFunctions, "validateHubSpotAuthCode");
export const validateHubSpotAuthCode = async (
  hubspotAuthCode: string,
  redirectUri?: string
): Promise<{
  result?: Organization;
  status: number;
}> => {
  const payload = redirectUri 
    ? { hubspotAuthCode, redirectUri }
    : hubspotAuthCode;
  try {
    const { data } = await validateHubSpotAuthCodeFunction(payload);
    if (!data) {
      throw new Error("Error validateHubSpotAuthCode: No data returned");
    }
    return data;
  } catch (error: any) {
    // Extract the actual error message from Firebase Functions
    const errorMessage = error?.message || error?.details || "Unknown error";
    console.error("HubSpot auth code validation error:", {
      error,
      message: errorMessage,
      code: error?.code,
      details: error?.details,
    });
    throw new Error(errorMessage);
  }
};

const setupProspectWebhookFunction = httpsCallable<
  never,
  { result?: { webhookId: string }; status: number }
>(firebaseFunctions, "setupProspectWebhook");
export const setupProspectWebhook = async (): Promise<{
  result?: { webhookId: string };
  status: number;
}> => {
  const { data } = await setupProspectWebhookFunction();
  if (!data) {
    throw new Error("Error setting up prospect webhook");
  }
  return data;
};

const getOutreachUsersFunction = httpsCallable<
  never,
  {
    result?: {
      users: OutreachUser[];
      included?: any[];
      relationshipsIncluded?: boolean;
    };
    status: number;
  }
>(firebaseFunctions, "getOutreachUsers");
export const getOutreachUsers = async (): Promise<{
  result?: {
    users: OutreachUser[];
    included?: any[];
    relationshipsIncluded?: boolean;
  };
  status: number;
}> => {
  const { data } = await withTimeout(getOutreachUsersFunction(), 300000);
  if (!data) {
    throw new Error("Error getting Outreach users");
  }
  return data;
};

// HubSpot contact properties for custom field dropdowns
export interface HubSpotContactProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  hubspotDefined: boolean;
}

const getHubSpotContactPropertiesFunction = httpsCallable<
  never,
  { result?: HubSpotContactProperty[]; status: number }
>(firebaseFunctions, "getHubSpotContactProperties");

export const getHubSpotContactProperties = async (): Promise<{
  result?: HubSpotContactProperty[];
  status: number;
}> => {
  const { data } = await withTimeout(getHubSpotContactPropertiesFunction(), 60000);
  if (!data) {
    throw new Error("Error getting HubSpot contact properties");
  }
  return data;
};

// Create a new HubSpot contact property
export interface CreateHubSpotPropertyParams {
  label: string;
  fieldType: 'text' | 'select' | 'number' | 'date' | 'textarea' | 'bool';
}

const createHubSpotContactPropertyFunction = httpsCallable<
  CreateHubSpotPropertyParams,
  { result?: { name: string; label: string }; status: number; error?: string }
>(firebaseFunctions, "createHubSpotContactProperty");

export const createHubSpotContactProperty = async (
  params: CreateHubSpotPropertyParams
): Promise<{
  result?: { name: string; label: string };
  status: number;
  error?: string;
}> => {
  const { data } = await withTimeout(createHubSpotContactPropertyFunction(params), 60000);
  if (!data) {
    throw new Error("Error creating HubSpot contact property");
  }
  return data;
};

// Job change settings (admins only; the server refuses anyone else)
const updateJobChangeSettingsFunction = httpsCallable<
  { organizationSettings: { jobChange: JobChangeSettings } },
  { result?: unknown; status: number; error?: { message?: string } }
>(firebaseFunctions, "updateOrgSettings");

export const updateJobChangeSettings = async (jobChange: JobChangeSettings): Promise<void> => {
  const { data } = await withTimeout(updateJobChangeSettingsFunction({ organizationSettings: { jobChange } }), 60000);
  if (!data || data.status !== 200) {
    throw new Error(data?.status === 403 ? "Only admins can change this." : data?.error?.message || "Could not save the job change settings.");
  }
};

// Outreach prospect stages, for the job change close-out picker
export interface OutreachStage {
  id: string;
  name: string;
  order: number | null;
}

const getOutreachProspectStagesFunction = httpsCallable<never, { result?: OutreachStage[] | null; status: number; error?: { message?: string; reason?: string } }>(
  firebaseFunctions,
  "getOutreachProspectStages"
);

export const getOutreachProspectStages = async (): Promise<{ stages: OutreachStage[]; error: string | null }> => {
  const { data } = await withTimeout(getOutreachProspectStagesFunction(), 60000);
  if (data?.status === 200 && Array.isArray(data.result)) return { stages: data.result, error: null };
  return { stages: [], error: data?.error?.message || "Could not read your Outreach stages." };
};

// HubSpot lead statuses
export interface HubSpotLeadStatus {
  value: string;
  label: string;
}

const getHubSpotLeadStatusesFunction = httpsCallable<
  never,
  { result?: HubSpotLeadStatus[]; status: number }
>(firebaseFunctions, "getHubSpotLeadStatuses");

export const getHubSpotLeadStatuses = async (): Promise<{
  result?: HubSpotLeadStatus[];
  status: number;
}> => {
  const { data } = await withTimeout(getHubSpotLeadStatusesFunction(), 60000);
  if (!data) {
    throw new Error("Error getting HubSpot lead statuses");
  }
  return data;
};

// HubSpot users
export interface HubSpotUserTeam {
  id: string;
  name: string;
  primary: boolean;
}

export interface HubSpotUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userId?: number;
  teams?: HubSpotUserTeam[];
  archived?: boolean;
  createdAt?: string;
  updatedAt?: string;
  // From Settings Users API
  roleId?: string;
  roleName?: string;
  avatarUrl?: string;
  superAdmin?: boolean;
}

const getHubSpotUsersFunction = httpsCallable<
  never,
  { result?: { users: HubSpotUser[] }; status: number }
>(firebaseFunctions, "getHubSpotUsers");

export const getHubSpotUsers = async (): Promise<{
  result?: { users: HubSpotUser[] };
  status: number;
}> => {
  const { data } = await withTimeout(getHubSpotUsersFunction(), 300000);
  if (!data) {
    throw new Error("Error getting HubSpot users");
  }
  return data;
};

// Outreach prospect custom fields
export interface OutreachCustomField {
  name: string;
  label: string;
  type: string;
  fieldNumber: number;
}

const getOutreachProspectCustomFieldsFunction = httpsCallable<
  never,
  { result?: OutreachCustomField[]; status: number }
>(firebaseFunctions, "getOutreachProspectCustomFields");

export const getOutreachProspectCustomFields = async (): Promise<{
  result?: OutreachCustomField[];
  status: number;
}> => {
  const { data } = await withTimeout(getOutreachProspectCustomFieldsFunction(), 60000);
  if (!data) {
    throw new Error("Error getting Outreach custom fields");
  }
  return data;
};

const getOrganizationIdByOutreachOrgIdFunction = httpsCallable<
  { outreachOrganizationId: string },
  { result?: { organizationId: string }; status: number }
>(firebaseFunctions, "getOrganizationIdByOutreachOrgId");
export const getOrganizationIdByOutreachOrgId = async ({
  outreachOrganizationId,
}: {
  outreachOrganizationId: string;
}): Promise<{
  result?: { organizationId: string };
  status: number;
}> => {
  const { data } = await getOrganizationIdByOutreachOrgIdFunction({
    outreachOrganizationId,
  });
  if (!data) {
    throw new Error("Error getting organization");
  }
  return data;
};

const getOrganizationByOutreachOrgIdFunction = httpsCallable<
  { outreachOrganizationId: string },
  { result?: { organization: EmbedOrganization | null }; status: number }
>(firebaseFunctions, "getOrganizationByOutreachOrgId");
export const getOrganizationByOutreachOrgId = async ({
  outreachOrganizationId,
}: {
  outreachOrganizationId: string;
}): Promise<{
  result?: { organization: EmbedOrganization | null };
  status: number;
}> => {
  const { data } = await getOrganizationByOutreachOrgIdFunction({
    outreachOrganizationId,
  });
  if (!data) {
    throw new Error("Error getting organization");
  }
  return data;
};

const getChartRangeStatsFunction = httpsCallable<
  { organizationId?: string; outreachOrganizationId?: string; startDate: string; endDate: string },
  { result?: StatsResult; status: number }
>(firebaseFunctions, "getChartRangeStats");
/**
 * Email verification stats for a range. Signed in, pass organizationId (the
 * caller must be a member). The public Outreach email report passes
 * outreachOrganizationId instead, and the server finds the organization.
 */
export const getChartRangeStats = async ({
  organizationId,
  outreachOrganizationId,
  startDate,
  endDate,
}: {
  organizationId?: string;
  outreachOrganizationId?: string;
  startDate: Dayjs;
  endDate: Dayjs;
}): Promise<StatsResult> => {
  const { data } = await getChartRangeStatsFunction({
    ...(outreachOrganizationId ? { outreachOrganizationId } : { organizationId }),
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  });
  if (!data?.result) {
    throw new Error("Error fetching chart stats");
  }
  return data.result;
};

// --------------------
// Billing
// --------------------

const getBillingStatusFunction = httpsCallable<
  Record<string, never>,
  { result?: BillingStatus; status: number }
>(firebaseFunctions, "getBillingStatus");
export const getBillingStatus = async (): Promise<BillingStatus> => {
  const { data } = await getBillingStatusFunction({});
  if (!data?.result) {
    throw new Error("Error fetching billing status");
  }
  return data.result;
};

const createBillingSetupIntentFunction = httpsCallable<
  Record<string, never>,
  {
    result?: { clientSecret: string; publishableKey: string };
    status: number;
  }
>(firebaseFunctions, "createBillingSetupIntent");
export const createBillingSetupIntent = async (): Promise<{
  clientSecret: string;
  publishableKey: string;
}> => {
  const { data } = await createBillingSetupIntentFunction({});
  if (!data?.result) {
    throw new Error("Error preparing payment form");
  }
  return data.result;
};

const startBillingSubscriptionFunction = httpsCallable<
  { interval: BillingInterval; paymentMethodId: string; plan?: BillingPlanKey; addDialFaster?: boolean },
  { result?: StartSubscriptionResult; status: number; error?: string }
>(firebaseFunctions, "startBillingSubscription");
export const startBillingSubscription = async (options: {
  interval: BillingInterval;
  paymentMethodId: string;
  plan?: BillingPlanKey;
  /** Live, Verified Data or LinkedIn Integration with the Dial faster add-on. */
  addDialFaster?: boolean;
}): Promise<StartSubscriptionResult> => {
  const { data } = await startBillingSubscriptionFunction(options);
  if (!data?.result) {
    throw new Error(data?.error || "Error starting subscription");
  }
  return data.result;
};

const cancelBillingSubscriptionFunction = httpsCallable<
  Record<string, never>,
  { result?: { success: boolean }; status: number }
>(firebaseFunctions, "cancelBillingSubscription");
export const cancelBillingSubscription = async (): Promise<void> => {
  const { data } = await cancelBillingSubscriptionFunction({});
  if (!data?.result?.success) {
    throw new Error("Error canceling subscription");
  }
};

const resumeBillingSubscriptionFunction = httpsCallable<
  Record<string, never>,
  { result?: { success: boolean }; status: number }
>(firebaseFunctions, "resumeBillingSubscription");
export const resumeBillingSubscription = async (): Promise<void> => {
  const { data } = await resumeBillingSubscriptionFunction({});
  if (!data?.result?.success) {
    throw new Error("Error resuming subscription");
  }
};

const changeBillingIntervalFunction = httpsCallable<
  { interval: BillingInterval },
  { result?: { success: boolean }; status: number }
>(firebaseFunctions, "changeBillingInterval");
export const changeBillingInterval = async (
  interval: BillingInterval
): Promise<void> => {
  const { data } = await changeBillingIntervalFunction({ interval });
  if (!data?.result?.success) {
    throw new Error("Error changing billing interval");
  }
};

const updateBillingPaymentMethodFunction = httpsCallable<
  { paymentMethodId: string },
  { result?: { success: boolean }; status: number }
>(firebaseFunctions, "updateBillingPaymentMethod");
export const updateBillingPaymentMethod = async (
  paymentMethodId: string
): Promise<void> => {
  const { data } = await updateBillingPaymentMethodFunction({
    paymentMethodId,
  });
  if (!data?.result?.success) {
    throw new Error("Error updating payment method");
  }
};

const changeBillingPlanFunction = httpsCallable<
  { plan: BillingPlanKey },
  { result?: { success: boolean }; status: number; error?: string }
>(firebaseFunctions, "changeBillingPlan");
export const changeBillingPlan = async (
  plan: BillingPlanKey
): Promise<void> => {
  const { data } = await changeBillingPlanFunction({ plan });
  if (!data?.result?.success) {
    throw new Error(data?.error || "Error changing plan");
  }
};

const addBillingSeatsFunction = httpsCallable<
  { additionalSeats: number },
  {
    result?: { seats: number; availableLicenses: number };
    status: number;
    error?: string;
  }
>(firebaseFunctions, "addBillingSeats");
export const addBillingSeats = async (
  additionalSeats: number
): Promise<{ seats: number; availableLicenses: number }> => {
  const { data } = await addBillingSeatsFunction({ additionalSeats });
  if (!data?.result) {
    throw new Error(data?.error || "Error adding licenses");
  }
  return data.result;
};

const listBillingInvoicesFunction = httpsCallable<
  Record<string, never>,
  { result?: { invoices: BillingInvoice[] }; status: number }
>(firebaseFunctions, "listBillingInvoices");
export const listBillingInvoices = async (): Promise<BillingInvoice[]> => {
  const { data } = await listBillingInvoicesFunction({});
  return data?.result?.invoices || [];
};

// --------------------
// Firestore
// --------------------

const usersCollection = collection(firebaseFirestore, "users");
const statsCollection = collection(firebaseFirestore, "stats");
const organizationsCollection = collection(firebaseFirestore, "organizations");

export const getDbUser = async (uid: UID): Promise<AppUser | null> => {
  const dbUser = (await getDoc(doc(usersCollection, uid))).data();
  return (dbUser as AppUser) || null;
};

/** Follows the signed-in user's own document (it is all theirs to read). */
export const subToDbUser = (uid: UID, callback: (user: AppUser | null) => void): Unsubscribe =>
  onSnapshot(doc(usersCollection, uid), (snapshot) => callback((snapshot.data() as AppUser) || null));

export const getDbOrganization = async (orgId: string): Promise<Organization | null> => {
  const orgDoc = await getDoc(doc(organizationsCollection, orgId));
  if (!orgDoc.exists()) {
    return null;
  }
  const orgData = orgDoc.data() as Organization;
  return { ...orgData, id: orgDoc.id };
};

export const upsertAppUser = async (user: AppUser): Promise<AppUser> => {
  // Use merge: true to avoid overwriting existing user data
  await setDocSafely(doc(usersCollection, user.uid), user, { merge: true });
  return user;
};

export const updateAppUser = async (
  user: Partial<AppUser>
): Promise<Partial<AppUser>> => {
  await setDocSafely(doc(usersCollection, user.uid), user, { merge: true });
  return user;
};

const getUniqueProspectCountsForDateRange = async ({
  allActivity,
  startDate,
  endDate,
}: {
  allActivity: CollectionReference<DocumentData>;
  startDate: Dayjs;
  endDate?: Dayjs;
}) => {
  const batchSize = 100;
  let lastDoc = null;
  const docs: Activity[] = [];
  let hasMore = true;

  while (hasMore) {
    // Create a paginated query
    const q: any = query(
      allActivity,
      where("time", ">=", startDate.toDate()),
      where("time", "<=", endDate?.toDate() ?? new Date()),
      orderBy("time", "desc"),
      limit(batchSize),
      ...(lastDoc ? [startAfter(lastDoc)] : []) // If we have a last doc, start after it
    );

    // Get the current batch of documents
    const snapshot = await getDocs(q);

    // Add the current batch to the accumulated results
    snapshot.forEach((doc) => {
      docs.push(doc.data() as Activity);
    });

    // Check if we've reached the end of the collection
    if (snapshot.size < batchSize) {
      hasMore = false; // No more batches
    } else {
      lastDoc = snapshot.docs[snapshot.docs.length - 1]; // Update the last doc to start after
    }
  }

  // Activity is ordered by time DESC, so the first record seen for a prospect is
  // its most recent one. Keep that and ignore earlier re-verifications, so a
  // prospect that was invalid at 9am and enriched to valid at 5pm counts as valid.
  const latestActivityByProspectId: Record<number, Activity> = {};
  const GuessedActivityUniquelyByProspectId: Record<number, Activity> = {};
  docs.forEach((data: Activity) => {
    const { prospectId, didGuess, status } = data;
    if (prospectId) {
      if (!latestActivityByProspectId[prospectId]) {
        latestActivityByProspectId[prospectId] = data;
      }

      // Count guessed prospects uniquely (once per prospect)
      if (
        status === OutreachEmailStatuses.Valid &&
        didGuess &&
        !GuessedActivityUniquelyByProspectId[prospectId]
      ) {
        GuessedActivityUniquelyByProspectId[prospectId] = data;
      }
    }
  });

  // Count by status, unique prospects only
  const countByStatus = Object.values(latestActivityByProspectId).reduce<{
    [key in OutreachEmailStatuses | ExtendedEmailStatuses]: number;
  }>(
    (acc, { status }) => {
      acc[status] = (acc[status] || 0) + 1;

      return acc;
    },
    {} as { [key in OutreachEmailStatuses | ExtendedEmailStatuses]: number }
  );

  // Count guessed prospects that were unique
  countByStatus[ExtendedEmailStatuses.EnrichedByBoring] = Object.values(
    GuessedActivityUniquelyByProspectId
  ).length;

  // Bounces prevented = unique prospects we caught as invalid (same as invalid count)
  countByStatus[ExtendedEmailStatuses.BouncesPrevented] =
    countByStatus[OutreachEmailStatuses.Invalid] || 0;
  return countByStatus;
};

export const getRangeStats = async ({
  organizationId,
  startDate: startDateArg,
  endDate: endDateArg,
}: {
  organizationId: string;
  startDate: Dayjs;
  endDate: Dayjs;
}): Promise<StatsResult> => {
  const statsRef: DocumentReference<DocumentData> = doc(
    statsCollection,
    organizationId
  );

  const allActivity = collection(statsRef, "activity");
  const dateRange = getStatsDateRange(startDateArg, endDateArg);

  const result: StatsResult = {};
  let promises: Promise<void>[] = [];

  const getStatsForDateRange = async (
    startDate: Dayjs,
    endDate: Dayjs,
    customKey?: string
  ) => {
    const key = customKey || endDate.toDate().toDateString();
    result[key] = {
      [OutreachEmailStatuses.Valid]: 0,
      [OutreachEmailStatuses.Invalid]: 0,
      [OutreachEmailStatuses.Questionable]: 0,
      [OutreachEmailStatuses.NoEmail]: 0,
      [ExtendedEmailStatuses.EnrichedByBoring]: 0,
      [ExtendedEmailStatuses.BouncesPrevented]: 0,
    };

    const data = await getUniqueProspectCountsForDateRange({
      allActivity,
      startDate,
      endDate,
    });

    result[key] = {
      ...result[key],
      ...data,
    };

    result[key].prospectsChecked = Object.entries(OutreachEmailStatuses).reduce(
      (acc, curr) => {
        const [, value] = curr;
        return acc + (result[key][value] || 0);
      },
      0
    );
  };

  promises.push(getStatsForDateRange(startDateArg, endDateArg, `totals`));

  for (const date of dateRange) {
    promises.push(getStatsForDateRange(date.startOf("day"), date.endOf("day")));

    // Calculate stats in batches of 10
    if (promises.length % 10 === 0) {
      await Promise.all(promises);
      promises = [];
    }
  }

  await Promise.all(promises); // Calculate remaining stats if any

  return result;
};

export const subToRunStats = (
  { organizationId }: { organizationId: string },
  observer: (snapshot: DocumentSnapshot<DocumentData>) => void
): Unsubscribe => {
  const docRef: DocumentReference<DocumentData> = doc(
    statsCollection,
    organizationId
  );

  return onSnapshot(
    docRef,
    { includeMetadataChanges: false },
    observer
  );
};

export function subToEmailsInQueue(
  organizationId: string,
  callback: (emailsInQueue: number) => void
) {
  try {
    const docRef = doc(firebaseFirestore, "stats", organizationId);
    const unsubscribe = onSnapshot(docRef, (doc) => {
      const emailsInQueue = doc.data()?.emailsInQueue || 0;
      callback(emailsInQueue);
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error listening to remaining emails", error);
  }
}

export interface RateLimitInfo {
  remaining: number;
  limit: number;
  reset: number | null;
}

// Live Outreach org-level API rate limit, written by the backend on each
// throttled read of Outreach's x-ratelimit-* headers. Null until first seen.
export function subToRateLimit(
  organizationId: string,
  callback: (rateLimit: RateLimitInfo | null) => void
) {
  try {
    const docRef = doc(firebaseFirestore, "stats", organizationId);
    const unsubscribe = onSnapshot(docRef, (doc) => {
      const rl = doc.data()?.rateLimit;
      callback(
        rl
          ? {
              remaining: rl.remaining ?? 0,
              limit: rl.limit ?? 0,
              reset: rl.reset ?? null,
            }
          : null
      );
    });
    return unsubscribe;
  } catch (error) {
    console.error("Error listening to rate limit", error);
  }
}

// In-memory request deduplication to prevent concurrent expensive queries
const pendingRequests = new Map<string, Promise<OrganizationStats>>();

// Cache today's data for 5 minutes to prevent thundering herd
const TODAY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Limit concurrent date fetches to prevent browser crashes on high-volume orgs (e.g. Riverside)
const CHART_FETCH_BATCH_SIZE = 5;

export const getRangeStatsFromCache = async ({
  organizationId,
  startDate: startDateArg,
  endDate: endDateArg,
}: {
  organizationId: string;
  startDate: Dayjs;
  endDate: Dayjs;
}): Promise<StatsResult> => {
  const statsRef: DocumentReference<DocumentData> = doc(
    statsCollection,
    organizationId
  );

  const graphDataCache = collection(statsRef, "chartDataCache");
  const dateRange = getStatsDateRange(startDateArg, endDateArg);
  const today = dayjs().startOf("day");

  const result: StatsResult = {};

  const fetchDateStats = async (date: Dayjs) => {
    const key = date.toDate().toDateString();
    const currentDate = dayjs(date).startOf("day");
    const isToday = currentDate.isSame(today, "day");
    
    // Create a unique request key for deduplication
    const requestKey = `${organizationId}_${key}`;
    
    try {
      // Check cache first (even for today, but with TTL check)
      let data: OrganizationStats | null = null;
      
      if (!isToday) {
        // For past dates, always check cache
        const cachedDoc = await getDoc(doc(graphDataCache, key));
        data = cachedDoc.data() as OrganizationStats | null;
      } else {
        // For today, check cache but verify it's fresh (within 5 minutes)
        const cachedDoc = await getDoc(doc(graphDataCache, key));
        const cachedData = cachedDoc.data() as (OrganizationStats & { cachedAt?: number }) | null;
        
        if (cachedData && cachedData.cachedAt) {
          const cacheAge = Date.now() - cachedData.cachedAt;
          if (cacheAge < TODAY_CACHE_TTL_MS) {
            data = cachedData;
          }
        }
      }

      // If cache miss, fetch data with request deduplication
      if (!data) {
        // Check if there's already a pending request for this key
        let fetchPromise = pendingRequests.get(requestKey);
        
        if (!fetchPromise) {
          // Create new fetch promise
          fetchPromise = (async () => {
            try {
              const statsResult = await getRangeStats({
                organizationId,
                startDate: currentDate.startOf("day"),
                endDate: currentDate.endOf("day"),
              });
              const fetchedData = statsResult.totals as OrganizationStats;
              
              // Cache the result (including today's data with timestamp)
              const dataToCache: OrganizationStats & { cachedAt?: number } = {
                ...fetchedData,
                ...(isToday && { cachedAt: Date.now() }),
              };
              
              // Cache asynchronously to avoid blocking
              setDoc(doc(graphDataCache, key), dataToCache, { merge: true }).catch((err) => {
                console.error(`Failed to cache stats for ${key}:`, err);
              });
              
              return fetchedData;
            } finally {
              // Remove from pending requests after completion
              pendingRequests.delete(requestKey);
            }
          })();
          
          pendingRequests.set(requestKey, fetchPromise);
        }
        
        // Wait for the fetch (either new or existing pending request)
        data = await fetchPromise;
      }

      return { key, data: data as OrganizationStats };
    } catch (error) {
      console.error(`Error fetching stats for ${key}:`, error);
      // Return empty stats on error to prevent crash
      return {
        key,
        data: {
          prospectsChecked: 0,
          enrichedByBoring: 0,
          bouncesPrevented: 0,
          invalid: 0,
          valid: 0,
          questionable: 0,
        } as OrganizationStats,
      };
    }
  };

  // Process dates in batches to avoid browser crashes on high-volume orgs
  const dateResults: { key: string; data: OrganizationStats }[] = [];
  for (let i = 0; i < dateRange.length; i += CHART_FETCH_BATCH_SIZE) {
    const batch = dateRange.slice(i, i + CHART_FETCH_BATCH_SIZE);
    const batchResults = await Promise.all(batch.map(fetchDateStats));
    dateResults.push(...batchResults);
  }
  
  // Build result object
  dateResults.forEach(({ key, data }) => {
    result[key] = data;
  });

  // Totals are computed over the whole range in one pass, NOT by summing the
  // per-day buckets. Each day dedupes prospects only within that day, so a
  // prospect verified on several days would be counted once per day.
  const rangeCounts = await getUniqueProspectCountsForDateRange({
    allActivity: collection(statsRef, "activity"),
    startDate: startDateArg,
    endDate: endDateArg,
  });

  const emptyTotals = {
    [OutreachEmailStatuses.Valid]: 0,
    [OutreachEmailStatuses.Invalid]: 0,
    [OutreachEmailStatuses.Questionable]: 0,
    [OutreachEmailStatuses.NoEmail]: 0,
    [ExtendedEmailStatuses.EnrichedByBoring]: 0,
    [ExtendedEmailStatuses.BouncesPrevented]: 0,
  };

  const totals: OrganizationStats = {
    ...emptyTotals,
    ...rangeCounts,
  } as OrganizationStats;

  totals.prospectsChecked = Object.values(OutreachEmailStatuses).reduce(
    (acc, value) => acc + ((totals[value] as number) || 0),
    0
  );

  result.totals = totals;

  return result;
};

// --------------------
// Dialer settings (boring extension)
// --------------------

const getDialerSettingsFunction = httpsCallable<
  never,
  { result: DialerSettingsResult | null; status: number }
>(firebaseFunctions, "getDialerSettings");
export const getDialerSettings = async (): Promise<DialerSettingsResult> => {
  const { data } = await getDialerSettingsFunction();
  if (!data?.result) {
    throw new Error("Could not load dialer settings");
  }
  return data.result;
};

const updateDialerSettingsFunction = httpsCallable<
  { settings: Partial<DialerSettings> },
  { result: DialerSettingsResult | null; status: number }
>(firebaseFunctions, "updateDialerSettings");
export const updateDialerSettings = async (
  settings: Partial<DialerSettings>
): Promise<DialerSettingsResult> => {
  const { data } = await updateDialerSettingsFunction({ settings });
  if (!data?.result) {
    throw new Error("Could not save dialer settings");
  }
  return data.result;
};

// --------------------
// Add-ons and usage (boring extension features)
// --------------------

const getAddonStatusFunction = httpsCallable<never, { result: AddonStatusResult | null; status: number }>(firebaseFunctions, "getAddonStatus");
export const getAddonStatus = async (): Promise<AddonStatusResult> => {
  const { data } = await getAddonStatusFunction();
  if (!data?.result) throw new Error("Could not load add-ons");
  return data.result;
};

const startAddonFunction = httpsCallable<{ addon: AddonKey; interval?: "monthly" | "yearly"; seats?: number }, { result: AddonStatusResult | null; status: number; error?: string }>(firebaseFunctions, "startAddon");
export const startAddon = async (addon: AddonKey, interval?: "monthly" | "yearly", seats?: number): Promise<AddonStatusResult> => {
  const { data } = await startAddonFunction({ addon, interval, seats });
  if (!data?.result) throw new Error(data?.error || "Could not start the add-on");
  return data.result;
};

const cancelAddonFunction = httpsCallable<{ addon: AddonKey }, { result: AddonStatusResult | null; status: number; error?: string }>(firebaseFunctions, "cancelAddon");
export const cancelAddon = async (addon: AddonKey): Promise<AddonStatusResult> => {
  const { data } = await cancelAddonFunction({ addon });
  if (!data?.result) throw new Error(data?.error || "Could not cancel the add-on");
  return data.result;
};

const setFeatureSeatsFunction = httpsCallable<{ addon: AddonKey; emails: string[] }, { result: AddonStatusResult | null; status: number; error?: string }>(firebaseFunctions, "setFeatureSeats");
export const setFeatureSeats = async (addon: AddonKey, emails: string[]): Promise<AddonStatusResult> => {
  const { data } = await setFeatureSeatsFunction({ addon, emails });
  if (!data?.result) throw new Error(data?.error || "Could not update seats");
  return data.result;
};

const getUsageReportFunction = httpsCallable<{ months?: number }, { result: UsageReport | null; status: number }>(firebaseFunctions, "getUsageReport");
export const getUsageReport = async (months = 6): Promise<UsageReport> => {
  const { data } = await getUsageReportFunction({ months });
  if (!data?.result) throw new Error("Could not load usage");
  return data.result;
};

// --------------------
// Caller ID health
// --------------------

const getCallerIdHealthFunction = httpsCallable<{ refresh?: boolean }, { result: CallerIdHealth | null; status: number }>(firebaseFunctions, "getCallerIdHealth");
export const getCallerIdHealth = async (refresh = false): Promise<CallerIdHealth> => {
  const { data } = await getCallerIdHealthFunction({ refresh });
  if (!data?.result) throw new Error("Could not load caller ID health");
  return data.result;
};

const recordCallerIdLabelFunction = httpsCallable<{ number: string; carrier: string; label: string | null }, { result: { numbers: CallerIdRecord[] } | null; status: number }>(firebaseFunctions, "recordCallerIdLabel");
export const recordCallerIdLabel = async (number: string, carrier: string, label: string | null): Promise<CallerIdRecord[]> => {
  const { data } = await recordCallerIdLabelFunction({ number, carrier, label });
  return data?.result?.numbers || [];
};

const setCallerIdRegisteredFunction = httpsCallable<{ number: string; registry: "fcr" | "hiya"; registered: boolean }, { result: { numbers: CallerIdRecord[] } | null; status: number }>(firebaseFunctions, "setCallerIdRegistered");
export const setCallerIdRegistered = async (number: string, registry: "fcr" | "hiya", registered: boolean): Promise<CallerIdRecord[]> => {
  const { data } = await setCallerIdRegisteredFunction({ number, registry, registered });
  return data?.result?.numbers || [];
};

// --------------------
// LinkedIn automation
// --------------------

const getLinkedInAutomationFunction = httpsCallable<{ days?: number }, { result: LinkedInAutomationView | null; status: number }>(firebaseFunctions, "getLinkedInAutomation");
export const getLinkedInAutomation = async (days = 30): Promise<LinkedInAutomationView> => {
  const { data } = await getLinkedInAutomationFunction({ days });
  if (!data?.result) throw new Error("Could not load automation");
  return data.result;
};

const saveLinkedInAutomationSettingsFunction = httpsCallable<Partial<LinkedInAutomationSettings>, { result: LinkedInAutomationSettings; status: number }>(firebaseFunctions, "saveLinkedInAutomationSettings");
export const saveLinkedInAutomationSettings = async (patch: Partial<LinkedInAutomationSettings>): Promise<LinkedInAutomationSettings> => {
  const { data } = await saveLinkedInAutomationSettingsFunction(patch);
  return data.result;
};

const syncLinkedInAutomationNowFunction = httpsCallable<never, { result: { queued: number; skipped: number }; status: number }>(firebaseFunctions, "syncLinkedInAutomationNow");
export const syncLinkedInAutomationNow = async (): Promise<{ queued: number; skipped: number }> => {
  const { data } = await syncLinkedInAutomationNowFunction();
  return data.result;
};

// --------------------
// Insights report and phone enrichment settings
// --------------------

const getInsightsReportFunction = httpsCallable<{ startDate: string; endDate: string }, { result: InsightsReport | null; status: number }>(firebaseFunctions, "getInsightsReport");
export const getInsightsReport = async (startDate: Date, endDate: Date): Promise<InsightsReport> => {
  const { data } = await getInsightsReportFunction({ startDate: startDate.toISOString(), endDate: endDate.toISOString() });
  if (!data?.result) throw new Error("Could not load insights");
  return data.result;
};

const getPhoneEnrichmentSettingsFunction = httpsCallable<unknown, { result: { settings: PhoneEnrichmentSettings } | null; status: number }>(firebaseFunctions, "getPhoneEnrichmentSettings");
export const getPhoneEnrichmentSettings = async (): Promise<PhoneEnrichmentSettings> => {
  const { data } = await getPhoneEnrichmentSettingsFunction({});
  if (!data?.result) throw new Error("Could not load phone enrichment settings");
  return data.result.settings;
};

const updatePhoneEnrichmentSettingsFunction = httpsCallable<{ settings: Partial<PhoneEnrichmentSettings> }, { result: { settings: PhoneEnrichmentSettings } | null; status: number }>(firebaseFunctions, "updatePhoneEnrichmentSettings");
export const updatePhoneEnrichmentSettings = async (settings: Partial<PhoneEnrichmentSettings>): Promise<PhoneEnrichmentSettings> => {
  const { data } = await updatePhoneEnrichmentSettingsFunction({ settings });
  if (!data?.result) throw new Error("Could not save phone enrichment settings");
  return data.result.settings;
};

const getExportSettingsFunction = httpsCallable<unknown, { result: ExportSettingsResult | null; status: number }>(firebaseFunctions, "getExportSettings");
export const getExportSettings = async (): Promise<ExportSettingsResult> => {
  const { data } = await getExportSettingsFunction({});
  if (!data?.result) throw new Error("Could not load export settings");
  return data.result;
};

const updateExportSettingsFunction = httpsCallable<{ settings: ExportMappings }, { result: ExportSettingsResult | null; status: number }>(firebaseFunctions, "updateExportSettings");
export const updateExportSettings = async (settings: ExportMappings): Promise<ExportSettingsResult> => {
  const { data } = await updateExportSettingsFunction({ settings });
  if (!data?.result) throw new Error("Could not save export settings");
  return data.result;
};

export interface ExportField {
  name: string;
  label: string;
  type: string;
  group?: string;
}
const listExportFieldsFunction = httpsCallable<{ destination: string; record: string }, { result: { fields: ExportField[]; error?: string } | null; status: number }>(firebaseFunctions, "listExportFields");
/** The fields an export can write on a record (HubSpot's writable properties); empty for other destinations. */
export const listExportFields = async (destination: string, record: string): Promise<{ fields: ExportField[]; error?: string }> => {
  const { data } = await listExportFieldsFunction({ destination, record });
  return data?.result || { fields: [], error: "Could not load fields." };
};

const createExportFieldFunction = httpsCallable<
  { destination: string; record: string; label: string; type?: string; taken?: string[] },
  { result: { field: ExportField } | null; status: number; error?: string }
>(firebaseFunctions, "createExportField");
/**
 * "+ Create new" in the export mapping: a new HubSpot property, or the first
 * Outreach custom field that is unnamed and unused (Outreach cannot create fields).
 */
export const createExportField = async (params: { destination: string; record: string; label: string; type?: string; taken?: string[] }): Promise<ExportField> => {
  const { data } = await withTimeout(createExportFieldFunction(params), 60000);
  if (!data?.result?.field) throw new Error(data?.error || "Could not create the field.");
  return data.result.field;
};

const listExportSequencesFunction = httpsCallable<unknown, { result: ExportSequencesResult | null; status: number }>(firebaseFunctions, "listExportSequences");
export const listExportSequences = async (): Promise<ExportSequencesResult> => {
  const { data } = await listExportSequencesFunction({});
  if (!data?.result) throw new Error("Could not load sequences");
  return data.result;
};

const listExportStagesFunction = httpsCallable<unknown, { result: ExportStagesResult | null; status: number }>(firebaseFunctions, "listExportStages");
/** Settings > Export > Default stage: HubSpot's lifecycle stages and lead statuses, or Outreach's prospect stages. */
export const listExportStages = async (): Promise<ExportStagesResult> => {
  const { data } = await listExportStagesFunction({});
  if (!data?.result) throw new Error("Could not load stages");
  return data.result;
};

const getExportHistoryFunction = httpsCallable<ExportHistoryQuery, { result: ExportHistoryResult | null; status: number }>(firebaseFunctions, "getExportHistory");
export const getExportHistory = async (query: ExportHistoryQuery = {}): Promise<ExportHistoryResult> => {
  const { data } = await getExportHistoryFunction(query);
  if (!data?.result) throw new Error("Could not load the export history");
  return data.result;
};

const listJobChangesFunction = httpsCallable<JobChangesQuery, { result: JobChangesResult | null; status: number }>(firebaseFunctions, "listJobChanges");
/** The Job changes page: rows by scope (Mine / Team), status, detected date and page, with the pending count. */
export const listJobChanges = async (query: JobChangesQuery = {}): Promise<JobChangesResult> => {
  const { data } = await listJobChangesFunction(query);
  if (!data?.result) throw new Error("Could not load job changes");
  return data.result;
};

const resolveJobChangesFunction = httpsCallable<{ keys: string[]; action: JobChangeAction }, { result: ResolveJobChangesResult | null; status: number }>(firebaseFunctions, "resolveJobChanges", { timeout: 540000 });
/** Approve (apply in the CRM) or reject up to 200 job changes; each row says what happened. */
export const resolveJobChanges = async (keys: string[], action: JobChangeAction): Promise<ResolveJobChangesResult> => {
  const { data } = await withTimeout(resolveJobChangesFunction({ keys, action }), 540000);
  if (!data?.result) throw new Error("Could not update the job changes");
  return data.result;
};

const getMyDialerSettingsFunction = httpsCallable<unknown, { result: MyDialerResult | null; status: number }>(firebaseFunctions, "getMyDialerSettings");
export const getMyDialerSettings = async (): Promise<MyDialerResult> => {
  const { data } = await getMyDialerSettingsFunction({});
  if (!data?.result) throw new Error("Could not load your dialer preferences");
  return data.result;
};

const updateMyDialerSettingsFunction = httpsCallable<{ settings: MyDialerSettings }, { result: MyDialerResult | null; status: number }>(firebaseFunctions, "updateMyDialerSettings");
export const updateMyDialerSettings = async (settings: MyDialerSettings): Promise<MyDialerResult> => {
  const { data } = await updateMyDialerSettingsFunction({ settings });
  if (!data?.result) throw new Error("Could not save your dialer preferences");
  return data.result;
};

// --------------------
// LinkedIn conversations, for the Outreach tab and tile
// --------------------

export type LinkedInThreadData = { messages: InboxMessage[]; canReply: boolean };
const getLinkedInConversationsFunction = httpsCallable<
  { prospectId?: string; linkedinUrl?: string; name?: string; participantUrn?: string },
  { result: { conversations: InboxConversation[]; threads?: Record<string, LinkedInThreadData> } | null; status: number }
>(firebaseFunctions, "getLinkedInConversations");
/** One person's conversations, and the newest few threads with them. */
export const getLinkedInConversations = async (query: {
  prospectId?: string;
  linkedinUrl?: string;
  name?: string;
  participantUrn?: string;
}): Promise<{ conversations: InboxConversation[]; threads: Record<string, LinkedInThreadData> }> => {
  const { data } = await getLinkedInConversationsFunction(query);
  return { conversations: data?.result?.conversations || [], threads: data?.result?.threads || {} };
};

const getLinkedInInboxFunction = httpsCallable<
  { syncedBy?: string; limit?: number },
  { result: { conversations: InboxConversation[]; reps: { uid: string; email: string | null }[] } | null; status: number }
>(firebaseFunctions, "getLinkedInInbox");
export const getLinkedInInbox = async (query: { syncedBy?: string } = {}): Promise<{ conversations: InboxConversation[]; reps: { uid: string; email: string | null }[] }> => {
  const { data } = await getLinkedInInboxFunction(query);
  return data?.result || { conversations: [], reps: [] };
};

const removeLinkedInConversationFunction = httpsCallable<{ conversationId: string }, { result: { removed: boolean } | null; status: number }>(
  firebaseFunctions,
  "removeLinkedInConversation"
);
/** Removes a conversation from boring for the team; LinkedIn is untouched. */
export const removeLinkedInConversation = async (conversationId: string): Promise<boolean> => {
  const { data } = await removeLinkedInConversationFunction({ conversationId });
  return !!data?.result?.removed;
};

const getLinkedInThreadFunction = httpsCallable<{ conversationId: string }, { result: { messages: InboxMessage[]; canReply: boolean } | null; status: number }>(
  firebaseFunctions,
  "getLinkedInThread"
);
export const getLinkedInThread = async (conversationId: string): Promise<{ messages: InboxMessage[]; canReply: boolean }> => {
  const { data } = await getLinkedInThreadFunction({ conversationId });
  return data?.result || { messages: [], canReply: false };
};

export interface SendLinkedInReplyInput {
  conversationId: string;
  linkedinUrl: string;
  text?: string;
  prospectId?: string;
  scheduledAt?: string;
  emoji?: string;
  messageId?: string;
  replyTo?: { messageUrn?: string; senderUrn?: string; senderName?: string; text?: string; sentAt?: string };
  attachments?: LinkedInAttachment[];
}

export interface LinkedInAttachment {
  url: string;
  name: string;
  mediaType: string;
  size: number;
}

/** Puts a file where the extension can fetch it when it sends the reply. */
export const uploadLinkedInAttachment = async (file: File): Promise<LinkedInAttachment> => {
  const uid = firebaseAuth.currentUser?.uid;
  if (!uid) throw new Error("Sign in to attach files");
  const safe = file.name.replace(/[^\w.-]+/g, "_").slice(0, 120) || "attachment";
  const target = storageRef(firebaseStorage, `linkedin-attachments/${uid}/${Date.now()}-${safe}`);
  await uploadBytes(target, file, { contentType: file.type || "application/octet-stream" });
  const url = await getDownloadURL(target);
  return { url, name: file.name || safe, mediaType: file.type || "application/octet-stream", size: file.size };
};

export interface GifResult {
  id: string;
  title: string;
  preview: string;
  previewWidth: number;
  previewHeight: number;
  full: string;
}
const searchGiphyFunction = httpsCallable<{ q?: string; limit?: number; offset?: number; memes?: boolean }, { result: { results: GifResult[]; hasMore: boolean } | null; status: number }>(
  firebaseFunctions,
  "searchGiphy"
);
export const searchGiphy = async (input: { q?: string; limit?: number; offset?: number; memes?: boolean }): Promise<{ results: GifResult[]; hasMore: boolean }> => {
  const { data } = await searchGiphyFunction(input);
  return data?.result || { results: [], hasMore: false };
};
const sendLinkedInReplyFunction = httpsCallable<SendLinkedInReplyInput, { result: { queued: boolean; scheduledAt?: number; reason?: string } | null; status: number }>(
  firebaseFunctions,
  "sendLinkedInReply"
);
export const sendLinkedInReply = async (input: SendLinkedInReplyInput): Promise<{ queued: boolean; scheduledAt?: number; reason?: string }> => {
  const { data } = await sendLinkedInReplyFunction(input);
  return data?.result || { queued: false, reason: "No response" };
};

const getProductLicenseRulesFunction = httpsCallable<unknown, { result: { rules: ProductLicenseRules } | null; status: number }>(firebaseFunctions, "getProductLicenseRules");
export const getProductLicenseRules = async (): Promise<ProductLicenseRules> => {
  const { data } = await getProductLicenseRulesFunction({});
  return data?.result?.rules || {};
};

const updateProductLicenseRulesFunction = httpsCallable<
  { addon: string; teams?: string[]; roles?: string[]; autoSync?: boolean },
  { result: { rules: ProductLicenseRules } | null; status: number }
>(firebaseFunctions, "updateProductLicenseRules");
export const updateProductLicenseRules = async (input: { addon: string; teams?: string[]; roles?: string[]; autoSync?: boolean }): Promise<ProductLicenseRules> => {
  const { data } = await updateProductLicenseRulesFunction(input);
  if (!data?.result) throw new Error("Could not save product licence rules");
  return data.result.rules;
};

const getProfileEnrichmentSettingsFunction = httpsCallable<unknown, { result: { settings: ProfileEnrichmentSettings } | null; status: number }>(firebaseFunctions, "getProfileEnrichmentSettings");
export const getProfileEnrichmentSettings = async (): Promise<ProfileEnrichmentSettings> => {
  const { data } = await getProfileEnrichmentSettingsFunction({});
  if (!data?.result) throw new Error("Could not load profile enrichment settings");
  return data.result.settings;
};

const updateProfileEnrichmentSettingsFunction = httpsCallable<{ standaloneLookup: boolean }, { result: { settings: ProfileEnrichmentSettings } | null; status: number }>(firebaseFunctions, "updateProfileEnrichmentSettings");
export const updateProfileEnrichmentSettings = async (standaloneLookup: boolean): Promise<ProfileEnrichmentSettings> => {
  const { data } = await updateProfileEnrichmentSettingsFunction({ standaloneLookup });
  if (!data?.result) throw new Error("Could not save profile enrichment settings");
  return data.result.settings;
};

// --------------------
// AI apps (boring's MCP connector): the consent page and Connected AI apps
// --------------------

const getMcpAuthorizationRequestFunction = httpsCallable<{ requestId: string }, { result: McpAuthorizationRequest; status: number }>(firebaseFunctions, "getMcpAuthorizationRequest");
export const getMcpAuthorizationRequest = async (requestId: string): Promise<McpAuthorizationRequest> => {
  const { data } = await getMcpAuthorizationRequestFunction({ requestId });
  if (!data?.result) throw new Error("This sign-in link has expired. Start connecting again from your AI app.");
  return data.result;
};

const decideMcpAuthorizationFunction = httpsCallable<{ requestId: string; approve: boolean; allowWrite: boolean }, { result: { redirectUrl: string }; status: number }>(
  firebaseFunctions,
  "decideMcpAuthorization"
);
/** Approve or decline; answers with where to send the browser (back to the AI app). */
export const decideMcpAuthorization = async (requestId: string, approve: boolean, allowWrite: boolean): Promise<string> => {
  const { data } = await decideMcpAuthorizationFunction({ requestId, approve, allowWrite });
  if (!data?.result?.redirectUrl) throw new Error("Could not finish connecting");
  return data.result.redirectUrl;
};

const listMcpConnectionsFunction = httpsCallable<unknown, { result: McpConnections; status: number }>(firebaseFunctions, "listMcpConnections");
export const listMcpConnections = async (): Promise<McpConnections> => {
  const { data } = await listMcpConnectionsFunction({});
  if (!data?.result) throw new Error("Could not load connected AI apps");
  return data.result;
};

const revokeMcpConnectionFunction = httpsCallable<{ grantId: string }, { result: { revoked: boolean }; status: number }>(firebaseFunctions, "revokeMcpConnection");
export const revokeMcpConnection = async (grantId: string): Promise<void> => {
  await revokeMcpConnectionFunction({ grantId });
};
